import { Selection, ScaleLinear, select } from 'd3';
import { LabTestsChartData, LabTestsChartYAxisTick, LastVisitInfo, LabTestsChartDataValue } from './lab-tests-chart-data-generator';
import { find, isNumber } from 'lodash';
import { CvdChartData, CvdBandDataValue } from '@covance/ng-cvd3';
import { FlaggedClassNames } from '../charts/chart-helper';

export class LabTestsChartSvgExtensions {
    private readonly testGroupNamePosition = {
        x: 12,
        y: 4
    };

    constructor(
        private readonly yAxisWidth: number,
        private readonly svgChart: Selection<SVGElement, LabTestsChartData[], HTMLElement, any>,
        private readonly yScale: ScaleLinear<number, number>,
        private readonly availableChartHeight: number,
        private readonly availableChartWidth: number,
        private readonly xAxisTickValues: number[],
        private readonly tests: LabTestsChartYAxisTick[],
        private readonly chartBandData: CvdChartData<CvdBandDataValue>[]
    ) { }

    public render() {
        this.removeYAxisLine();
        this.removeXAxisLine();
        this.moveXAxisLabelsOnTop();
        this.handleXAxisLabelOverflow();
        this.appendAdditionalYAxisText();
        this.calculateRanges();
        this.calculatePointsColors();
    }

    private removeYAxisLine() {
        this.svgChart.selectAll<SVGGElement, number>('.cv-y.cv-axis path.domain').remove();
    }

    private removeXAxisLine() {
        this.svgChart.selectAll<SVGGElement, number>('.cv-x.cv-axis path.domain').remove();
    }

    private moveXAxisLabelsOnTop() {
        const xAxisLayer = this.svgChart.select('.cv-x.cv-axis');
        const heightText = 20;
        const height = this.availableChartHeight;

        xAxisLayer.attr('transform', function(this: SVGGElement) {
            const translate = parseTransformTranslate(this);
            return `translate(${translate.x},${height - heightText})`;
        });

        xAxisLayer
            .selectAll<SVGTextElement, number>('g.tick text')
            .attr('transform', `translate(0,-${this.availableChartHeight})`);

        xAxisLayer
            .selectAll<SVGLineElement, number>('g.tick line')
            .attr('transform', () => `translate(0, ${heightText})`);
    }

    private handleXAxisLabelOverflow() : void {
        const width = this.availableChartWidth / this.xAxisTickValues.length;

        this.svgChart
            .selectAll('.cv-x g.tick')
            .attr('width', width);

        this.svgChart.selectAll('.cv-x g.tick text').each(function() {
            const selectedTextNode = select(this);

            let text = selectedTextNode.text();

            selectedTextNode.style('pointer-events', 'all').text('').append('title').text(text);
            selectedTextNode.append('tspan').text(text);

            const tspan = selectedTextNode.select('tspan');

            let textLength = (tspan.node() as SVGTSpanElement).getComputedTextLength();

            while (textLength > width && textLength > 0) {
                text = text.slice(0, -1);
                tspan.text(text + '\u2026');
                textLength = (tspan.node() as SVGTSpanElement).getComputedTextLength();
            }
        });
    }

    private appendAdditionalYAxisText() {
        const yAxis = this.svgChart.select<SVGGElement>('.cv-y.cv-axis');
        const yAxisTicks = this.svgChart.selectAll<SVGGElement, number>('.cv-y.cv-axis g.tick');

        yAxisTicks.selectAll<SVGTextElement, number>('text').attr('opacity', 1).classed('test-y-tick-text', true);

        this.svgChart.selectAll<SVGTextElement, number>('text.last-visit-value').remove();

        const getLastVisitValue = (yTickValue: number) => this.getLastVisitValue(yTickValue);

        yAxisTicks
            .append<SVGTextElement>('text')
            .classed('last-visit-value', true)
            .text((yTickValue, index) => {
                const lastVisitValue = this.getLastVisitValue(yTickValue);
                return lastVisitValue ? lastVisitValue.value : null;
            })
            .each(function(yTickValue) {
                const lastVisitValue = getLastVisitValue(yTickValue);

                if (lastVisitValue) {
                    select(this).classed(lastVisitValue.colorClassName, true);
                }
            })
            .attr('transform', () => `translate(${this.availableChartWidth},4)`);

        this.svgChart.selectAll<SVGTextElement, number>('text.test-group-name').remove();

        const getYAxisTickData = (value: number) => {
            const yAxisTickData = find(this.tests, test => test.value === value);
            return yAxisTickData;
        };

        yAxisTicks
            .append('text')
            .classed('test-group-name', true)
            .style('text-anchor', 'end')
            .text(value => {
                const yAxisTickData = getYAxisTickData(value);
                return yAxisTickData ? yAxisTickData.testGroupName : null;
            });

        yAxisTicks.select('line').remove();
        const testGroupNamePosition = this.testGroupNamePosition;
        const testGroupNamePositionX = -this.yAxisWidth + this.testGroupNamePosition.x;
        yAxisTicks
        .selectAll('text.test-group-name')
        .style('text-anchor', 'unset')
        .attr('transform', function(this: SVGTextElement) {
                const g = this as SVGGElement;
                const gBox = g.getBBox();
                return `translate(${testGroupNamePositionX + gBox.x}, ${testGroupNamePosition.y})`;
        });

        yAxisTicks
            .each(function(value) {
                const yAxisTickData = getYAxisTickData(value);

                if (!yAxisTickData || !yAxisTickData.testGroupName) {
                    return;
                }

                const yAxisBox = yAxis.node().getBBox();
                const box = this.getBBox();

                select(this).append('line')
                    .attr('y1', box.y)
                    .attr('y2', box.y)
                    .attr('x1', 0)
                    .attr('x1', yAxisBox.x);
            });

        this.ellipsisTestGroups(yAxisTicks);
    }

    private ellipsisTestGroups(yAxisTicks: Selection<SVGGElement, number, SVGElement, LabTestsChartData[]>) {
        const isTestOverlapped = this.isTestOverlapped;

        yAxisTicks.each(function() {
            const testTextD3 = select(this).select<SVGTextElement>('.test-y-tick-text');
            const groupNameTextD3 = select(this).select<SVGTextElement>('.test-group-name');
            const testText = testTextD3.node();
            const groupNameText = groupNameTextD3.node();

            if (!testText || !groupNameText || !testTextD3.text() || !groupNameTextD3.text()) {
                return;
            }

            if (isTestOverlapped(groupNameText, testText)) {
                const groupName = groupNameTextD3.text();
                let fragment = '';

                for (let pos = 0; pos < groupName.length; pos++) {
                    fragment = groupName.substring(0, groupName.length - pos) + '...';
                    groupNameTextD3.text(fragment);

                    if (!isTestOverlapped(groupNameText, testText)) {
                        break;
                    }
                }

                groupNameTextD3
                    .style('pointer-events', 'all')
                    .append('title')
                    .text(groupName);
            }
        });
    }

    private isTestOverlapped = (groupNameText: SVGTextElement, testText: SVGTextElement): boolean => {
        const groupNameRect = groupNameText.getBoundingClientRect();
        const testRect = testText.getBoundingClientRect();

        return groupNameRect.left + groupNameRect.width > testRect.left;
    }

    private getLastVisitValue(yTickValue: number): LastVisitInfo {
        const yTick = find(this.tests, test => test.value === yTickValue);

        return yTick && yTick.lastVisitInfo
            ? yTick.lastVisitInfo
            : null;
    }

    private calculateRanges() {
        this.svgChart.selectAll('.test-range').remove();

        const transform = this.svgChart.select('g.cv-linesWrap')
            .attr('transform');

        const translateCoords = this.parseTranslate(transform);
        const isBandDataActual = this.isBandDataActual;
        const yScale = this.yScale;
        const width = this.availableChartWidth;

        const areas = this.svgChart.selectAll<SVGPathElement, CvdChartData<CvdBandDataValue>>('path.cv-area.d3-area')
            .each(function (bandData) {
                if (!bandData || !isBandDataActual(bandData)) {
                    return;
                }

                const bandValue = bandData.values[0];

                if (!bandValue) {
                    return;
                }

                const g = this.parentNode as SVGGElement;
                const x = translateCoords ? translateCoords.x * -1 : 0;
                const y1 = yScale(bandValue.y1);
                const y2 = yScale(bandValue.y2);

                const height = y1 - y2;
                const y = y2;

                select(g)
                    .append('rect')
                    .classed('test-range', true)
                    .attr('x', x)
                    .attr('y', y)
                    .attr('width', width)
                    .attr('height', height);
            });
    }

    private isBandDataActual = (bandData: CvdChartData<CvdBandDataValue>): boolean => {
        return this.chartBandData.some(b => b.key === bandData.key);
    }

    private parseTranslate(value: string): {x: number, y: number} {
        const result = /translate\((.+),(.+)\)/.exec(value);

        if (!result) {
            return null;
        }

        const [translateGroup, xGroup, yGroup] = result;
        const x = Number(xGroup);
        const y = Number(yGroup);

        if (!isNumber(x) || !isNumber(y)) {
            return null;
        }

        return { x, y };
    }

    private calculatePointsColors() {
        this.svgChart.selectAll<SVGPathElement, LabTestsChartDataValue>('.cv-scatter g.cv-groups g.cv-group path.cv-point')
            .classed(FlaggedClassNames.high, false)
            .classed(FlaggedClassNames.low, false)
            .classed(FlaggedClassNames.other, false)
            .classed(FlaggedClassNames.nonFlagged, false)
            .each(function (data) {
                select(this).classed(data.colorClassName, true);
            });
    }
}

function parseTransformTranslate(element: SVGGraphicsElement): { x: number, y: number } {
    const svgTransform = element.transform.baseVal.consolidate();

    if (!svgTransform) {
        return {
            x: 0,
            y: 0
        };
    }

    const matrix = svgTransform.matrix;

    return {
        x: matrix.e,
        y: matrix.f
    };
}